import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PhygitalLogo } from "@/components/PhygitalIcon";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  CheckCircle2,
  Clock,
  Mail,
  Sparkles,
  Palette,
  Calendar,
  Users,
  BarChart3,
  Bell,
  Gift,
  ArrowRight,
  Scissors,
  Heart,
  ChefHat,
  GraduationCap,
  FileSignature,
  ClipboardEdit,
  Download,
  ExternalLink,
  Copy,
  X,
  Save,
  Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Category icon mapping
const CATEGORY_ICONS = {
  makeup: Palette,
  mua: Palette,
  hair: Scissors,
  nail: Sparkles,
  lash: Sparkles,
  wellness: Heart,
  event: Calendar,
  events: Calendar,
  instructor: GraduationCap,
  culinary: ChefHat
};

// Category colors
const CATEGORY_COLORS = {
  makeup: { bg: "bg-pink-500", text: "text-pink-500", light: "bg-pink-50" },
  mua: { bg: "bg-pink-500", text: "text-pink-500", light: "bg-pink-50" },
  hair: { bg: "bg-purple-500", text: "text-purple-500", light: "bg-purple-50" },
  nail: { bg: "bg-rose-500", text: "text-rose-500", light: "bg-rose-50" },
  lash: { bg: "bg-violet-500", text: "text-violet-500", light: "bg-violet-50" },
  wellness: { bg: "bg-emerald-500", text: "text-emerald-500", light: "bg-emerald-50" },
  event: { bg: "bg-amber-500", text: "text-amber-500", light: "bg-amber-50" },
  events: { bg: "bg-amber-500", text: "text-amber-500", light: "bg-amber-50" },
  instructor: { bg: "bg-blue-500", text: "text-blue-500", light: "bg-blue-50" },
  culinary: { bg: "bg-orange-500", text: "text-orange-500", light: "bg-orange-50" }
};

// What's coming next - features to look forward to
const UPCOMING_FEATURES = [
  {
    icon: Users,
    title: "Client Preference Cards",
    description: "See your clients' preferences before they arrive"
  },
  {
    icon: Calendar,
    title: "Smart Scheduling",
    description: "Integrated booking with automated reminders"
  },
  {
    icon: BarChart3,
    title: "Business Insights",
    description: "Track your growth and client satisfaction"
  },
  {
    icon: Bell,
    title: "Real-time Updates",
    description: "Get notified when clients update their preferences"
  }
];

export default function PilotConfirmationPage() {
  const { portalId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [portalData, setPortalData] = useState(null);
  const [userData, setUserData] = useState(null);
  
  // Edit Intake Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [intakeData, setIntakeData] = useState(null);
  const [editResponses, setEditResponses] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/api/pilot/portal/${portalId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        if (res.ok) {
          const data = await res.json();
          setPortalData(data.portal);
          setUserData(data.user);
        }
      } catch (err) {
        console.error("Failed to fetch portal data:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [portalId]);
  
  // Fetch intake data for editing
  const loadIntakeForEdit = async () => {
    setEditLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/pilot/portal/${portalId}/intake-template`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (res.ok) {
        const data = await res.json();
        setIntakeData(data);
        // Merge prefill, existing_responses from API, and portalData.intake_responses
        setEditResponses({ 
          ...data.prefill, 
          ...data.existing_responses,
          ...portalData?.intake_responses  // Also include current portal responses
        });
        setShowEditModal(true);
      } else {
        toast.error("Failed to load intake data");
      }
    } catch (err) {
      console.error("Failed to load intake:", err);
      toast.error("Error loading intake data");
    } finally {
      setEditLoading(false);
    }
  };
  
  // Save edited responses
  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/pilot/portal/${portalId}/intake`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ 
          intake_responses: editResponses,
          intake_completed: true 
        })
      });
      
      if (res.ok) {
        toast.success("Responses updated successfully!");
        setShowEditModal(false);
        // Refresh the portal data to show updated responses
        const refreshRes = await fetch(`${API}/api/pilot/portal/${portalId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          setPortalData(data.portal);
          setUserData(data.user);
        }
      } else {
        toast.error("Failed to save changes");
      }
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Error saving changes");
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  const pilotType = portalData?.pilot_type || portalData?.category_type || "hair";
  const CategoryIcon = CATEGORY_ICONS[pilotType] || Sparkles;
  const colors = CATEGORY_COLORS[pilotType] || CATEGORY_COLORS.hair;
  const firstName = userData?.first_name || portalData?.first_name || "there";

  // Onboarding status
  const status = {
    accepted: true,
    ndaSigned: portalData?.nda_signed || false,
    intakeComplete: portalData?.intake_completed || true, // They're on this page, so intake is done
    dashboardReady: false // Dashboard is being prepared
  };

  // Handle download NDA
  const handleDownloadNDA = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/pilot/portal/${portalId}/nda`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.nda?.pdf_data) {
          // Create download link from base64 PDF data
          const link = document.createElement('a');
          link.href = data.nda.pdf_data;
          link.download = `Phygital_NDA_Signed_${data.nda.signature_name?.replace(/\s+/g, '_') || 'User'}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else if (data.nda?.pdf_url) {
          window.open(data.nda.pdf_url, "_blank");
        } else {
          console.log("NDA data not available");
          toast.error("NDA download not available");
        }
      } else {
        console.log("NDA not available for download");
        toast.error("Failed to download NDA");
      }
    } catch (err) {
      console.error("Error downloading NDA:", err);
      toast.error("Error downloading NDA");
    }
  };

  // Open edit intake modal (instead of navigating)
  const handleEditIntake = () => {
    loadIntakeForEdit();
  };
  
  // Update a single response field
  const handleEditChange = (questionId, value) => {
    setEditResponses(prev => ({ ...prev, [questionId]: value }));
  };
  
  // Copy confirmation page link
  const handleCopyLink = () => {
    const confirmationUrl = `${window.location.origin}/pilot/confirmation/${portalId}`;
    navigator.clipboard.writeText(confirmationUrl).then(() => {
      toast.success("Link copied to clipboard!");
    }).catch(() => {
      toast.error("Failed to copy link");
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <PhygitalLogo className="h-6" />
          <Badge variant="outline" className={`${colors.text} border-current`}>
            <CategoryIcon className="w-3 h-3 mr-1" />
            Pilot Partner
          </Badge>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className={`w-20 h-20 ${colors.light} rounded-full flex items-center justify-center mx-auto mb-6`}>
            <CheckCircle2 className={`w-10 h-10 ${colors.text}`} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">
            You're all set, {firstName}!
          </h1>
          <p className="text-gray-600 text-lg max-w-md mx-auto mb-4">
            We're building your personalized experience. You'll be notified via email when your dashboard is ready.
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleCopyLink}
            className="mt-2"
            data-testid="copy-confirmation-link-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy This Page Link
          </Button>
        </div>

        {/* Onboarding Status */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-400" />
              Your Onboarding Status
            </h2>
            
            <div className="space-y-4">
              {/* Step 1: Accepted */}
              <div className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${status.accepted ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <CheckCircle2 className={`w-5 h-5 ${status.accepted ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Invite Accepted</p>
                  <p className="text-sm text-gray-500">Welcome to the pilot program</p>
                </div>
                <div className="flex items-center gap-2">
                  {portalData?.invite_token && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => window.open(`/pilot/invite/${portalData.invite_token}`, "_blank")}
                      className="text-xs"
                      data-testid="view-invite-btn"
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      View Invite
                    </Button>
                  )}
                  <Badge variant={status.accepted ? "default" : "secondary"} className={status.accepted ? "bg-green-500" : ""}>
                    Complete
                  </Badge>
                </div>
              </div>

              {/* Step 2: NDA Signed - Clickable */}
              <div className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${status.ndaSigned ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <FileSignature className={`w-5 h-5 ${status.ndaSigned ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div className="flex-1">
                  <p className="font-medium">NDA Signed</p>
                  <p className="text-sm text-gray-500">Confidentiality agreement completed</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => status.ndaSigned ? handleDownloadNDA() : navigate(`/pilot/nda/${portalId}`)}
                    className="text-xs"
                    data-testid="nda-action-btn"
                  >
                    {status.ndaSigned ? (
                      <><Download className="w-3 h-3 mr-1" />Download</>
                    ) : (
                      <><ExternalLink className="w-3 h-3 mr-1" />Sign</>
                    )}
                  </Button>
                  <Badge variant={status.ndaSigned ? "default" : "secondary"} className={status.ndaSigned ? "bg-green-500" : ""}>
                    {status.ndaSigned ? "Complete" : "Pending"}
                  </Badge>
                </div>
              </div>

              {/* Step 3: Intake Complete - Clickable */}
              <div className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${status.intakeComplete ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <ClipboardEdit className={`w-5 h-5 ${status.intakeComplete ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Setup Complete</p>
                  <p className="text-sm text-gray-500">Business details and preferences saved</p>
                </div>
                <div className="flex items-center gap-2">
                  {status.intakeComplete && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleEditIntake}
                      disabled={editLoading}
                      className="text-xs"
                      data-testid="edit-intake-btn"
                    >
                      {editLoading ? (
                        <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Loading...</>
                      ) : (
                        <><ClipboardEdit className="w-3 h-3 mr-1" />Edit</>
                      )}
                    </Button>
                  )}
                  <Badge variant="default" className="bg-green-500">
                    Complete
                  </Badge>
                </div>
              </div>

              {/* Step 4: Dashboard Being Built */}
              <div className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-purple-100`}>
                  <Sparkles className="w-5 h-5 text-purple-600 animate-pulse" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Building Your Dashboard</p>
                  <p className="text-sm text-gray-500">We're customizing your experience</p>
                </div>
                <Badge variant="outline" className="text-purple-600 border-purple-300">
                  In Progress
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Intake Responses Summary */}
        {portalData?.intake_responses && Object.keys(portalData.intake_responses).length > 0 && (
          <Card className="mb-8">
            <CardContent className="p-6">
              <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <ClipboardEdit className={`w-5 h-5 ${colors.text}`} />
                Your Submitted Responses
              </h2>
              <div className="space-y-3">
                {Object.entries(portalData.intake_responses)
                  .filter(([key]) => key.startsWith("q_") && !key.includes("portal_"))
                  .map(([key, value]) => {
                    if (!value || (Array.isArray(value) && value.length === 0)) return null;
                    const label = key.replace("q_", "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                    return (
                      <div key={key} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0" data-testid={`response-${key}`}>
                        <span className="text-sm text-gray-500 w-40 shrink-0 pt-0.5">{label}</span>
                        <span className="text-sm font-medium text-gray-900">
                          {Array.isArray(value) ? value.join(", ") : String(value)}
                        </span>
                      </div>
                    );
                  }).filter(Boolean)}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Email Notification */}
        <Card className={`mb-8 border-2 ${colors.light} border-transparent`} style={{ borderColor: 'currentColor' }}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 ${colors.light} rounded-xl flex items-center justify-center shrink-0`}>
                <Mail className={`w-6 h-6 ${colors.text}`} />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Check Your Email</h3>
                <p className="text-gray-600">
                  We'll send you an email at <span className="font-medium">{userData?.email || "your email"}</span> when your personalized dashboard is ready.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What's Coming - Visual Mockups */}
        <div className="mb-12">
          <h2 className="text-xl font-bold mb-6 text-center">What's Coming</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {UPCOMING_FEATURES.map((feature, idx) => {
              const FeatureIcon = feature.icon;
              return (
                <Card key={idx} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className={`w-10 h-10 ${colors.light} rounded-lg flex items-center justify-center mb-3`}>
                      <FeatureIcon className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <h3 className="font-semibold mb-1">{feature.title}</h3>
                    <p className="text-sm text-gray-500">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Pilot Partner Benefits */}
        <Card className="mb-8 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Gift className="w-6 h-6 text-yellow-400" />
              <h3 className="font-semibold text-lg">Pilot Partner Benefits</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-gray-200">
                <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                <span>Free Legacy tier access for life</span>
              </li>
              <li className="flex items-center gap-2 text-gray-200">
                <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                <span>Direct input on features we build</span>
              </li>
              <li className="flex items-center gap-2 text-gray-200">
                <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                <span>Priority support and onboarding</span>
              </li>
              <li className="flex items-center gap-2 text-gray-200">
                <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                <span>Early access to all new features</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t mt-12 py-6 text-center text-sm text-gray-500">
        <p>© {new Date().getFullYear()} Phygital. All rights reserved.</p>
      </footer>
      
      {/* Edit Intake Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardEdit className="w-5 h-5" />
              Edit Your Responses
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="h-[60vh] pr-4">
            {editLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Loading your responses...</span>
              </div>
            ) : intakeData?.sections?.length > 0 ? (
              <div className="space-y-6">
                {intakeData.sections.map((section, sectionIndex) => (
                  <div key={section.id || sectionIndex} className="border rounded-lg p-4">
                    <h3 className="font-semibold text-lg mb-4 pb-2 border-b">
                      {section.title}
                    </h3>
                    <div className="space-y-4">
                      {section.questions?.map((q, qIndex) => {
                        const questionId = q.id || `q_${sectionIndex}_${qIndex}`;
                        const value = editResponses[questionId] || editResponses[q.id?.replace('q_', '')] || "";
                        
                        return (
                          <div key={questionId} className="space-y-1.5">
                            <Label className="text-sm font-medium">
                              {q.label || q.question}
                              {q.required && <span className="text-red-500 ml-1">*</span>}
                            </Label>
                            
                            {q.type === "textarea" || q.type === "text_area" ? (
                              <Textarea
                                value={value}
                                onChange={(e) => handleEditChange(questionId, e.target.value)}
                                placeholder={q.placeholder || "Enter your response..."}
                                className="min-h-[80px]"
                              />
                            ) : q.type === "select" || q.type === "dropdown" ? (
                              <select
                                value={value}
                                onChange={(e) => handleEditChange(questionId, e.target.value)}
                                className="w-full border rounded-md p-2"
                              >
                                <option value="">Select an option</option>
                                {q.options?.map((opt, i) => (
                                  <option key={i} value={opt.value || opt}>
                                    {opt.label || opt}
                                  </option>
                                ))}
                              </select>
                            ) : q.type === "checkbox" || q.type === "multi_select" ? (
                              <div className="flex flex-wrap gap-2">
                                {q.options?.map((opt, i) => {
                                  const optValue = opt.value || opt;
                                  const currentValues = Array.isArray(value) ? value : value ? value.split(',') : [];
                                  const isChecked = currentValues.includes(optValue);
                                  
                                  return (
                                    <label key={i} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                          const newValues = e.target.checked
                                            ? [...currentValues, optValue]
                                            : currentValues.filter(v => v !== optValue);
                                          handleEditChange(questionId, newValues);
                                        }}
                                        className="rounded"
                                      />
                                      <span className="text-sm">{opt.label || opt}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            ) : (
                              <Input
                                type={q.type === "email" ? "email" : q.type === "phone" ? "tel" : "text"}
                                value={value}
                                onChange={(e) => handleEditChange(questionId, e.target.value)}
                                placeholder={q.placeholder || "Enter your response..."}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                No intake questions found
              </div>
            )}
          </ScrollArea>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
